import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { AuthCard } from "@/components/auth/AuthCard";
import { FormField } from "@/components/auth/FormField";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
});

type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { forgot } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      const email = values.email.trim().toLowerCase();
      await forgot(email);
      toast.success("If an account exists, we sent a reset code.");
      navigate(`/auth/reset?email=${encodeURIComponent(email)}`);
    } catch (err) {
      const message = (err as { message?: string }).message || "Something went wrong.";
      setError("email", { message });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <AuthCard
      title="Reset your password"
      description="Enter your email and we'll send you a 6-digit reset code."
      footer={
        <>
          Remembered it?{" "}
          <Link to="/auth/login" className="font-medium text-brand hover:underline">
            Back to sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormField id="email" label="Email" error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoFocus
            placeholder="you@example.com"
            aria-invalid={!!errors.email}
            {...register("email")}
          />
        </FormField>
        <Button type="submit" fullWidth loading={submitting}>
          Send reset code
        </Button>
      </form>
    </AuthCard>
  );
}
